import React from 'react'
import { Box, Chip, CircularProgress, Tooltip } from '@mui/material'
import { LightspeedIcon, XeroIcon, SkimmerIcon } from './SystemIcons'
import { generateLightspeedMainUrl, generateXeroMainUrl } from '../utils/urls'
import { formatTimeAgo } from '../utils/tableFormatters'
import { getFreshnessTier, type Tier } from '../utils/syncFreshness'
import useSyncFreshness, { type FreshnessSystem } from '../hooks/useSyncFreshness'

interface SystemStatusChipsProps {
  fpCode: string
  lightspeedStoreCode?: string
  xeroShortCode?: string
  isUSFranchise: boolean
  activeSyncs?: { sync_system: string }[]
}

interface TierStyle {
  backgroundColor: string
  borderColor: string
  color: string
  hoverBackground: string
}

const TIER_STYLES: Record<Tier, TierStyle> = {
  fresh: {
    backgroundColor: '#FFFFFF',
    borderColor: '#E0E0E0',
    color: '#333',
    hoverBackground: '#f5f5f5',
  },
  warning: {
    backgroundColor: '#FFF8E1',
    borderColor: '#FFB300',
    color: '#7A4F01',
    hoverBackground: '#FFECB3',
  },
  attention: {
    backgroundColor: '#FFF3E0',
    borderColor: '#FB8C00',
    color: '#5D2E00',
    hoverBackground: '#FFE0B2',
  },
  critical: {
    backgroundColor: '#FFEBEE',
    borderColor: '#D32F2F',
    color: '#8B0000',
    hoverBackground: '#FFCDD2',
  },
}

const tierTooltip = (tier: Tier, lastSync: string | null): string => {
  if (!lastSync) return 'Never synced'
  const iso = new Date(lastSync).toLocaleString()
  switch (tier) {
    case 'fresh':
      return `Last synced ${iso}`
    case 'warning':
      return `Sync recommended - last synced ${iso}`
    case 'attention':
      return `Sync before reviewing - last synced ${iso}`
    case 'critical':
      return `Data is stale - last synced ${iso}`
  }
}

const SystemStatusChips: React.FC<SystemStatusChipsProps> = ({
  fpCode,
  lightspeedStoreCode,
  xeroShortCode,
  isUSFranchise,
  activeSyncs = []
}) => {
  const { systems, loading } = useSyncFreshness(fpCode)

  const getSystemSyncStatus = (system: string): FreshnessSystem | undefined =>
    systems.find(s => s.system.toLowerCase() === system.toLowerCase())

  const isSystemSyncing = (system: string): boolean => {
    const status = getSystemSyncStatus(system)
    if (status?.is_active) return true
    return activeSyncs.some(s => s.sync_system.toLowerCase() === system.toLowerCase())
  }

  const handleChipClick = (system: string) => {
    if (system === 'lightspeed' && lightspeedStoreCode) {
      window.open(generateLightspeedMainUrl(lightspeedStoreCode), '_blank')
    } else if (system === 'xero' && xeroShortCode) {
      window.open(generateXeroMainUrl(xeroShortCode), '_blank')
    }
  }

  const renderChip = (
    system: string,
    icon: React.ReactNode,
    storeCode?: string,
    isClickable: boolean = true
  ) => {
    const status = getSystemSyncStatus(system)
    const syncing = isSystemSyncing(system)
    const lastSync = status?.last_sync ?? null
    const tier = getFreshnessTier(lastSync)
    const style = TIER_STYLES[tier]
    const timeAgo = lastSync ? formatTimeAgo(lastSync) : 'never'

    const chipIcon = syncing ? (
      <CircularProgress size={14} sx={{ color: '#007f98' }} />
    ) : (
      icon
    )

    const tooltipText = syncing ? `${system} sync in progress` : tierTooltip(tier, lastSync)

    return (
      <Tooltip title={tooltipText} key={system}>
        <Chip
          icon={<Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5 }}>{chipIcon}</Box>}
          label={timeAgo}
          size="small"
          onClick={isClickable && storeCode ? () => handleChipClick(system) : undefined}
          sx={{
            backgroundColor: style.backgroundColor,
            borderColor: style.borderColor,
            color: style.color,
            border: '1px solid',
            cursor: isClickable && storeCode ? 'pointer' : 'default',
            fontWeight: 600,
            fontSize: '0.75rem',
            height: 28,
            '& .MuiChip-icon': {
              marginLeft: '4px'
            },
            '&:hover': isClickable && storeCode ? {
              backgroundColor: style.hoverBackground,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            } : {}
          }}
        />
      </Tooltip>
    )
  }

  if (loading) {
    return null
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {lightspeedStoreCode && renderChip(
        'lightspeed',
        <LightspeedIcon size={16} />,
        lightspeedStoreCode,
        true
      )}

      {xeroShortCode && renderChip(
        'xero',
        <XeroIcon size={16} />,
        xeroShortCode,
        true
      )}

      {isUSFranchise && renderChip(
        'skimmer',
        <SkimmerIcon size={16} />,
        undefined,
        false
      )}
    </Box>
  )
}

export default SystemStatusChips
